'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useCollections } from './CollectionsContext';
import { useSession } from '@/lib/auth-client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import clsx from 'clsx';
interface AddToCollectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId: string | null;
  userCollections?: Array<{
    id: number;
    name: string;
  }>; // Collections the item is already in
  onCollectionChange?: (collectionId: number, collectionName: string, isAdding: boolean) => void; // Callback for optimistic updates
}
export function AddToCollectionModal({
  isOpen,
  onClose,
  productId,
  userCollections = [],
  onCollectionChange
}: AddToCollectionModalProps) {
  const {
    collections,
    createCollection,
    addToCollection,
    removeFromCollection
  } = useCollections();
  const {
    data: session
  } = useSession();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [newCollectionVisibility, setNewCollectionVisibility] = useState<'public' | 'private'>('private');
  const [processingCollections, setProcessingCollections] = useState<Set<string>>(new Set());
  const [optimisticState, setOptimisticState] = useState<Map<string, boolean>>(new Map());
  const [displayCollections, setDisplayCollections] = useState<typeof collections>([]);

  // Track the last known userCollections to detect when server state updates
  const prevUserCollectionsRef = useRef<typeof userCollections>([]);
  const hasInitializedRef = useRef(false);
  const isApproved = (session?.user as any)?.approved === true;
  const isUser = !!(session?.user as any);

  // Sort collections only when dialog first opens - lock sort order while open
  useEffect(() => {
    if (isOpen && !hasInitializedRef.current) {
      // Dialog just opened - sort collections based on initial userCollections
      const sorted = [...collections].sort((a, b) => {
        const numericIdA = Number(a.id);
        const numericIdB = Number(b.id);
        const aInCollection = userCollections.some(c => c.id === numericIdA);
        const bInCollection = userCollections.some(c_0 => c_0.id === numericIdB);

        // Sort collections with item first
        if (aInCollection && !bInCollection) return -1;
        if (!aInCollection && bInCollection) return 1;
        return 0;
      });
      setDisplayCollections(sorted);
      prevUserCollectionsRef.current = userCollections;
      hasInitializedRef.current = true;
    } else if (isOpen && hasInitializedRef.current) {
      // Dialog is open - add any new collections to the end without re-sorting
      const displayIds = new Set(displayCollections.map(c_1 => c_1.id));
      const newCollections = collections.filter(c_2 => !displayIds.has(c_2.id));
      if (newCollections.length > 0) {
        setDisplayCollections(prev => [...prev, ...newCollections]);
      }
    } else if (!isOpen) {
      // Dialog closed - clear everything for fresh state next time
      setDisplayCollections([]);
      setOptimisticState(new Map());
      prevUserCollectionsRef.current = [];
      hasInitializedRef.current = false;
    }
  }, [isOpen, collections, userCollections]);

  // Clear optimistic state when userCollections updates (server state has arrived)
  useEffect(() => {
    if (isOpen && prevUserCollectionsRef.current !== userCollections) {
      setOptimisticState(new Map());
      prevUserCollectionsRef.current = userCollections;
    }
  }, [isOpen, userCollections]);

  // Check if item is in a collection - use optimistic state if available, otherwise server state
  const isInCollection = (collectionId: string) => {
    const numericId = Number(collectionId);

    // Check optimistic state first (for immediate UI feedback)
    if (optimisticState.has(collectionId)) {
      return optimisticState.get(collectionId)!;
    }

    // Fall back to server state
    return userCollections.some(c_3 => c_3.id === numericId);
  };
  const handleToggleCollection = async (collectionId_0: string) => {
    if (!productId || !isApproved) return;
    const currentState = isInCollection(collectionId_0);
    const newState = !currentState;

    // Set optimistic state immediately
    setOptimisticState(prev_0 => {
      const next = new Map(prev_0);
      next.set(collectionId_0, newState);
      return next;
    });
    setProcessingCollections(prev_1 => new Set(prev_1).add(collectionId_0));

    // Notify parent component for optimistic updates
    const collection = collections.find(c_4 => c_4.id === collectionId_0);
    if (collection && onCollectionChange) {
      onCollectionChange(Number(collectionId_0), collection.name, newState);
    }
    try {
      if (currentState) {
        await removeFromCollection(collectionId_0, productId);
      } else {
        await addToCollection(collectionId_0, productId);
      }
      // Mutation completed successfully - cache should be updated
      // The useEffect will clear optimistic state when userCollections updates
    } catch (error) {
      console.error('Failed to toggle collection:', error);
      // Revert optimistic state on error
      setOptimisticState(prev_2 => {
        const next_0 = new Map(prev_2);
        next_0.set(collectionId_0, currentState);
        return next_0;
      });
      // Revert parent component's optimistic update
      if (collection && onCollectionChange) {
        onCollectionChange(Number(collectionId_0), collection.name, currentState);
      }
    } finally {
      setProcessingCollections(prev => {
        const next = new Set(prev);
        next.delete(collectionId_0);
        return next;
      });
    }
  };
  const handleCreateAndAdd = async () => {
    if (newCollectionName.trim() && productId && isApproved) {
      const newCollection = await createCollection(newCollectionName.trim(), newCollectionVisibility);
      if (newCollection) {
        await addToCollection(newCollection.id, productId);
        setNewCollectionName('');
        setShowCreateForm(false);
      }
    }
  };
  const handleClose = () => {
    setShowCreateForm(false);
    setNewCollectionName('');
    setNewCollectionVisibility('private');
    onClose();
  };
  return <Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
      <DialogContent className="h-full w-full md:max-w-md md:h-[90vh] flex flex-col p-0" fullscreenOnMobile>
        <DialogHeader className="px-6 pt-6 flex-shrink-0">
          <DialogTitle>{showCreateForm ? 'Create Collection' : 'Add to Collection'}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {showCreateForm ? <div className="space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="collectionName">Collection Name</Label>
                <Input id="collectionName" type="text" value={newCollectionName} onChange={e => setNewCollectionName(e.target.value)} placeholder="My Favorites" autoFocus />
              </div>

              <div className="grid gap-2">
                <Label>Visibility</Label>
                <div className="flex gap-3">
                  <button type="button" onClick={() => setNewCollectionVisibility('private')} className={clsx('flex-1 px-4 py-3 rounded-xl border-2 transition-all flex items-center justify-center gap-2', newCollectionVisibility === 'private' ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 hover:border-gray-300')}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Private
                  </button>
                  <button type="button" onClick={() => setNewCollectionVisibility('public')} className={clsx('flex-1 px-4 py-3 rounded-xl border-2 transition-all flex items-center justify-center gap-2', newCollectionVisibility === 'public' ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 hover:border-gray-300')}>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Public
                  </button>
                </div>
              </div>

              {session && !isApproved && <p className="text-xs text-center text-red-500 font-medium">
                  Account pending approval to create collections
                </p>}
            </div> : <div className="space-y-3">
              {collections.length === 0 ? <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0 a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <p className="text-gray-600 mb-4">No collections yet</p>
                </div> : <div className="space-y-2">
                  {displayCollections.map(collection_0 => {
              const inCollection = isInCollection(collection_0.id);
              const isProcessing = processingCollections.has(collection_0.id);
              return <div key={collection_0.id} className={clsx('w-full p-4 rounded-xl border-2 transition-all flex items-center justify-between gap-3', inCollection ? 'border-primary-200 bg-primary-50' : 'border-gray-200 hover:border-gray-300')}>
                        <button onClick={() => handleToggleCollection(collection_0.id)} disabled={!isApproved || isProcessing} className="flex-1 text-left flex items-center gap-3">
                          <div className={clsx('w-5 h-5 rounded border-2 flex items-center justify-center transition-all flex-shrink-0', inCollection ? 'border-primary-500 bg-primary-500' : 'border-gray-300 bg-white')}>
                            {inCollection && <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-gray-900">{collection_0.name}</span>
                              {collection_0.visibility === 'private' ? <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg> : <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>}
                            </div>
                          </div>
                        </button>
                        <Link href={`/collections/${collection_0.id}`} onClick={e_0 => e_0.stopPropagation()} className="p-2 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0" title="Open collection">
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </Link>
                      </div>;
            })}
                </div>}
            </div>}
        </div>

        {showCreateForm ? <DialogFooter className="px-6 pb-6 pt-4 border-t flex-shrink-0">
            <Button variant="outline" onClick={() => setShowCreateForm(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateAndAdd} disabled={!newCollectionName.trim() || !isApproved}>
              Create & Add
            </Button>
          </DialogFooter> : <div className="px-6 pb-6 pt-4 border-t flex-shrink-0">
            <Button variant="outline" onClick={() => {
          if (isApproved) setShowCreateForm(true);
        }} disabled={!isApproved} className="w-full border-dashed border-2 h-12">
              {isApproved ? 'Create New Collection' : isUser ? 'Pending account approval' : 'Sign in to create a collection'}
            </Button>
          </div>}
      </DialogContent>
    </Dialog>;
}